// src/store/slices/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthResponse } from '../services/userService';
import { jwtDecode } from 'jwt-decode';
import { api } from '../services/api';

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  user: AuthResponse['user'] | null;
}

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  user: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<AuthResponse>) => {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      localStorage.setItem('token', token);
    },
    logout: (state) => {
      // First remove token from localStorage to prevent any new API calls
      localStorage.clear();
      
      // Then clear auth state
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      
      // Finally reset API state
      // This needs to happen after auth state is cleared to prevent re-fetches
      api.util.resetApiState();
    },
    checkAuth: (state) => {
      const token = state.token;
      if (!token) {
        state.isAuthenticated = false;
        return;
      }

      try {
        const decoded: { exp: number } = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          state.token = null;
          state.user = null;
          state.isAuthenticated = false;
          localStorage.removeItem('token');
        } else {
          state.isAuthenticated = true;
        }
      } catch {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
      }
    },
  },
});

export const { setCredentials, logout, checkAuth } = authSlice.actions;
export default authSlice.reducer;

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;